import { AxiosResponse } from "axios";
import { IApiResponse } from "src/shared/models/IApiResponse";
import axiosApiInstance from "src/api/axiosInstance.api";
import { BlobOptions } from "buffer";
import { ScheduleTerminalUpdate } from "src/models/POS/Terminal.models";
import axiosInstanceFile from "./axiosInstanceFile";
import axiosInstanceFileJSON from "./axiosInstanceFileJSON";
import { IBase64Datum } from "src/models/shared/IImageDatumModel";
import { IIsOpen } from "src/models/General/General.models";

const responseBody = (response: AxiosResponse) => response.data;
const config = {
  headers: {
  }
};
const requests = {
  get: (url: string, params?: any) =>
    axiosApiInstance.get(url, { params: params }).then(responseBody),
  post: (url: string, body: any) =>
    axiosApiInstance.post(url, body).then(responseBody),
  put: (url: string, body: any) =>
    axiosApiInstance.put(url, body).then(responseBody),
  putNoBody: (url: string) =>
    axiosApiInstance.put(url).then(responseBody),
  del: (url: string) => axiosApiInstance.delete(url).then(responseBody),
  postFile: (url: string, body: FormData) => axiosInstanceFile.post(url, body).then(responseBody),
  postFile64: (url: string, body: IBase64Datum) => axiosInstanceFileJSON.post(url, body).then(responseBody)
};

export interface ISingleApiResponse<T> extends IApiResponse {
  data: T;
}

export interface IListApiResponse<T> extends IApiResponse {
  data: T[];
}

class GenericApiService<T> {
  getAdvises(endpoint: string): Promise<ISingleApiResponse<any>> {
    return requests.get(`/${endpoint}/advises`);
  }
  getAll(endpoint: string, params?: any): Promise<IListApiResponse<T>> {
    return requests.get(`/${endpoint}/getAll`, params);
  }
  getAllByRoute(
    endpoint: string,
    route: string,
    params?: any
  ): Promise<IListApiResponse<T>> {
    return requests.get(`/${endpoint}/${route}`, params);
  }
  postAllByRouteParamList(
    endpoint: string,
    route: string,
    body?: any
  ): Promise<IListApiResponse<T>> {
    return requests.post(`/${endpoint}/${route}`, body);
  }
  getAllByRouteParamList(
    endpoint: string,
    route: string,
    body?: any
  ): Promise<IListApiResponse<T>> {
    return requests.get(`/${endpoint}/${route}`, body);
  }
  getById(endpoint: string, id: number): Promise<ISingleApiResponse<T>> {
    return requests.get(`/${endpoint}/getById/${id}`);
  }
  getByCode(endpoint: string, id: string): Promise<ISingleApiResponse<T>> {
    return requests.get(`/${endpoint}/getByCode/${id}`);
  }
  getDocument(endpoint: string, id: number): Promise<ISingleApiResponse<string>> {
    return requests.putNoBody(`/${endpoint}/invoiceDocument/${id}`);
  }
  getDocumentHTML(endpoint: string, id: number): Promise<ISingleApiResponse<string>> {
    return requests.putNoBody(`/${endpoint}/invoiceDocumentHTML/${id}`);
  }
  getByIdString(endpoint: string, id: string): Promise<ISingleApiResponse<T>> {
    return requests.get(`/${endpoint}/getById/${id}`);
  }
  getByIdByAndRoute(
    endpoint: string,
    route: string,
    id: number
  ): Promise<ISingleApiResponse<T>> {
    return requests.get(`/${endpoint}/${route}/${id}`);
  }
  duplicate(endpoint: string, id: number): Promise<ISingleApiResponse<T>> {
    return requests.putNoBody(`/${endpoint}/duplicate/${id}`);
  }
  confirm(endpoint: string, id: number): Promise<ISingleApiResponse<T>> {
    return requests.putNoBody(`/${endpoint}/confirm/${id}`);
  }
  reverse(endpoint: string, id: number): Promise<ISingleApiResponse<T>> {
    return requests.putNoBody(`/${endpoint}/reverse/${id}`);
  }
  create(endpoint: string, body: T): Promise<ISingleApiResponse<T>> {
    return requests.post(`/${endpoint}/create`, body);
  }
  createWithRoute(endpoint: string, route: string,body: T): Promise<ISingleApiResponse<T>> {
    return requests.post(`/${endpoint}/${route}`, body);
  }
  createMultipleWithRoute(endpoint: string, route: string,body: T[]): Promise<IListApiResponse<T>> {
    return requests.post(`/${endpoint}/${route}`, body);
  }
  createMultiple(endpoint: string, body: any): Promise<IListApiResponse<T>> {
    return requests.post(`/${endpoint}/create-multiple`, body);
  }
  updateByRoute(endpoint: string, route: string, body: T): Promise<IListApiResponse<T>> {
    return requests.put(`/${endpoint}/${route}`, body);
  }
  updateByRouteSingle(endpoint: string, route: string, body: T): Promise<ISingleApiResponse<T>> {
    return requests.put(`/${endpoint}/${route}`, body);
  }
  update(endpoint: string, body: T): Promise<ISingleApiResponse<T>> {
    return requests.put(`/${endpoint}/update`, body);
  }
  setIsOpen(endpoint: string, id: number, isOpen: boolean): Promise<ISingleApiResponse<IIsOpen>> {
    return requests.putNoBody(`/${endpoint}/setIsOpen/${id}/${isOpen}`)
  }
  revokeIsOpenForCurrentUser(endpoint: string): Promise<ISingleApiResponse<IIsOpen>> {
    return requests.putNoBody(`/${endpoint}/revokeIsOpenForCurrentUser`)
  }
  restore(endpoint: string, body: number): Promise<ISingleApiResponse<T>> {
    return requests.put(`/${endpoint}/restore`, body);
  }
  updateBL(endpoint: string, body: T): Promise<ISingleApiResponse<T>> {
    return requests.put(`/${endpoint}/updateBL`, body);
  }
  updateMultiple(endpoint: string, body: any): Promise<IListApiResponse<T>> {
    return requests.put(`/${endpoint}/update`, body);
  }
  deleteMultiple(endpoint: string, body: any): Promise<IListApiResponse<T>> {
    return requests.put(`/${endpoint}/delete-multiple`, body);
  }
  deleteById(endpoint: string, id: number): Promise<ISingleApiResponse<T>> {
    return requests.del(`/${endpoint}/delete/${id}`);
  }
  schedule(
    endpoint: string,
    body: ScheduleTerminalUpdate
  ): Promise<ISingleApiResponse<T>> {
    return requests.post(`/${endpoint}/scheduleUpdate`, body);
  }
  upload(endpoint: string, validateWeight: boolean, body: FormData): Promise<ISingleApiResponse<T>> {
    return requests.postFile(`/${endpoint}/UploadFile/${validateWeight}`, body)
  }
  uploadB64(endpoint: string, body: any): Promise<ISingleApiResponse<T>> {
    return requests.postFile64(`/${endpoint}/UploadBase64`, body)
  }
}

export { GenericApiService };
